import { clientSessionViewStore } from '../../services/clientSessionViewStore.js'
import {
  ensureSessionCapability,
  SESSION_HEADER,
} from '../core/session/session-identity.js'
import type { CanonicalSessionPermissionsV1 } from '../core/session/session-projection.js'
import type { SessionProjectionEnvelopeV1 } from '../core/session/session-projection.js'
import { logger } from './logger.js'
import { visitorEmailsMatch } from './visitor-session-isolation.js'

export interface PersistIntakeIdentityParams {
  sessionId: string
  name: string
  email: string
  companyUrl?: string
  allowResearch?: boolean
  permissions?: CanonicalSessionPermissionsV1
  uiLocale?: 'en' | 'nb'
}

/**
 * Write concierge name/email/company onto the canonical projection via /api/consent.
 * Used when the UI briefing card shows identity from local form/transcript while the
 * server projection may still be empty (kill-window / restore / confirm-only paths).
 */
export async function persistIntakeIdentityToServer(
  params: PersistIntakeIdentityParams,
): Promise<{ ok: boolean; envelope?: SessionProjectionEnvelopeV1 }> {
  const sessionId = params.sessionId.trim()
  const name = params.name.trim()
  const email = params.email.trim()
  if (!sessionId || !name || !email) {
    return { ok: false }
  }

  try {
    await ensureSessionCapability(sessionId)
    const response = await fetch('/api/consent', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [SESSION_HEADER]: sessionId,
      },
      body: JSON.stringify({
        sessionId,
        name,
        email,
        ...(params.companyUrl ? { companyUrl: params.companyUrl } : {}),
        allowResearch: params.allowResearch === true,
        ...(params.permissions ? { permissions: params.permissions } : {}),
        ...(params.uiLocale ? { uiLocale: params.uiLocale } : {}),
      }),
    })
    const payload: unknown = await response.json().catch(() => ({}))
    if (!response.ok) {
      logger.warn('[persistIntakeIdentity] consent failed', {
        sessionId,
        status: response.status,
        error:
          payload && typeof payload === 'object' && !Array.isArray(payload)
            ? String((payload as Record<string, unknown>).error ?? 'unknown')
            : 'unknown',
      })
      return { ok: false }
    }

    const envelope =
      payload &&
      typeof payload === 'object' &&
      !Array.isArray(payload) &&
      (payload as Record<string, unknown>).projectionEnvelope &&
      typeof (payload as Record<string, unknown>).projectionEnvelope === 'object'
        ? ((payload as Record<string, unknown>).projectionEnvelope as SessionProjectionEnvelopeV1)
        : undefined

    if (envelope) {
      clientSessionViewStore.acceptServerProjection(envelope)
    }

    return { ok: true, ...(envelope ? { envelope } : {}) }
  } catch (error) {
    logger.warn('[persistIntakeIdentity] consent threw', {
      sessionId,
      error: error instanceof Error ? error.message : String(error),
    })
    return { ok: false }
  }
}

export function serverVisitorHasIdentity(): boolean {
  const projection = clientSessionViewStore.getServerProjectionSnapshot()?.projection
  if (!projection) return false
  const name = projection.visitor.name?.trim()
  const email = projection.visitor.email?.trim()
  return Boolean(name && email)
}

/** True only when the committed visitor is this intake email, not a prior occupant. */
export function serverVisitorMatchesIntake(email: string): boolean {
  const projection = clientSessionViewStore.getServerProjectionSnapshot()?.projection
  if (!projection) return false
  const name = projection.visitor.name?.trim()
  const visitorEmail = projection.visitor.email?.trim()
  return Boolean(name && visitorEmail && visitorEmailsMatch(visitorEmail, email))
}
